import { useCallback } from "react";
import { AddToStashtButton } from "@/components/shared/AddToStashtButton";
import {
  StashThumbnailGrid,
  StashThumbnailMasonry,
  type StashThumbnailData,
} from "@/components/shared/StashThumbnail";
import { Continuation } from "./SearchResults";
import { useExploreSearch } from "./useSearchSources";
import { CARD_TITLE, META } from "@/lib/typography";
import { cn } from "@/lib/utils";

/**
 * Explore's answer to a query: other people's public stashes, drawn as every stash list
 * is (masonry, or the grid on a phone), each with Add, as Explore draws them elsewhere.
 */
export function ExploreResults({ query, viewMode }: { query: string; viewMode: "masonry" | "grid" }) {
  const explore = useExploreSearch(query);
  const posts = explore.posts as unknown as StashThumbnailData[];
  const addOverlay = useCallback((post: StashThumbnailData) => <AddToStashtButton postId={post.id} />, []);
  const PostsView = viewMode === "masonry" ? StashThumbnailMasonry : StashThumbnailGrid;

  if (explore.isError) {
    return (
      <p className={cn(META, "w-full rounded border border-destructive/40 bg-destructive/5 p-4 text-destructive")}>
        Couldn't search Explore. Pull to refresh or try again soon.
      </p>
    );
  }
  if (posts.length === 0) {
    return explore.isFetching ? (
      <h3 className={cn(CARD_TITLE, "loading-dots py-12 text-center")}>Searching</h3>
    ) : (
      <p className={cn(CARD_TITLE, "py-12 text-center text-foreground")}>No matches</p>
    );
  }
  return (
    <div className="w-full">
      <PostsView posts={posts} getItemOverlay={addOverlay} />
      <Continuation
        more={!!explore.hasNextPage}
        loading={explore.isFetchingNextPage}
        onMore={() => void explore.fetchNextPage()}
        label="Loading more"
      />
    </div>
  );
}
